import { IconType } from 'react-icons';
import {
    TbAlignLeft,
    TbAlphabetLatin,
    TbArrowsCross,
    TbCalendarEvent,
    TbCalendarExclamation,
    TbCalendarMinus,
    TbCalendarMonth,
    TbCircleCheckFilled,
    TbEye,
    TbFlag,
    TbLayoutList,
    TbProgress,
    TbRefresh,
    TbSortAscending,
    TbSortDescending,
    TbUserCheck,
    TbUsers,
} from 'react-icons/tb';

import type { Task } from '@/types';
import { TaskParticipantRole, TaskPriority } from '@/types';
import { dayjs } from '@/utilities';

import { TASK_BUCKET_KEYS, taskBucketFor, type TaskBucketKey } from './buckets';

export const TASK_DISPLAYED_PROPERTY_KEYS = [
    'description',
    'status',
    'priority',
    'dueDate',
    'assignees',
    'reviewers',
    'associations',
    'visibility',
    'createdAt',
    'updatedAt',
] as const;

export type TaskDisplayedPropertyKey = (typeof TASK_DISPLAYED_PROPERTY_KEYS)[number];

export interface TaskDisplayedPropertyOption {
    description: string;
    icon: IconType;
    key: TaskDisplayedPropertyKey;
    label: string;
}

export const TASK_DISPLAYED_PROPERTY_OPTIONS: TaskDisplayedPropertyOption[] = [
    {
        key: 'description',
        label: 'Description',
        description: 'A compact preview of the task description.',
        icon: TbAlignLeft,
    },
    { key: 'status', label: 'Status', description: 'The current workflow status.', icon: TbProgress },
    { key: 'priority', label: 'Priority', description: 'The task priority and its indicator.', icon: TbFlag },
    { key: 'dueDate', label: 'Due Date', description: 'The date by which the task is due.', icon: TbCalendarEvent },
    {
        key: 'assignees',
        label: 'Assignees',
        description: 'Members responsible for completing the task.',
        icon: TbUsers,
    },
    {
        key: 'reviewers',
        label: 'Reviewers',
        description: 'Members responsible for reviewing the task.',
        icon: TbUserCheck,
    },
    {
        key: 'associations',
        label: 'Associations',
        description: 'Workspace resources connected to the task.',
        icon: TbArrowsCross,
    },
    { key: 'visibility', label: 'Visibility', description: 'Who can discover and access the task.', icon: TbEye },
    { key: 'createdAt', label: 'Created', description: 'When the task was created.', icon: TbCalendarEvent },
    { key: 'updatedAt', label: 'Updated', description: 'When the task was last changed.', icon: TbRefresh },
];

export const DEFAULT_TASK_DISPLAYED_PROPERTIES: TaskDisplayedPropertyKey[] = [
    'status',
    'priority',
    'dueDate',
    'assignees',
    'updatedAt',
];

export const TASK_GROUP_BY_VALUES = [
    'dueDate',
    'status',
    'priority',
    'assignee',
    'reviewer',
    'visibility',
    'createdAt',
    'updatedAt',
    'none',
] as const;

export type TaskGroupBy = (typeof TASK_GROUP_BY_VALUES)[number];

export const TASK_SORT_BY_VALUES = ['updatedAt', 'dueDate', 'title', 'priority', 'status', 'createdAt'] as const;

export type TaskSortBy = (typeof TASK_SORT_BY_VALUES)[number];

export const TASK_SORT_DIRECTION_VALUES = ['ascending', 'descending'] as const;

export type TaskSortDirection = (typeof TASK_SORT_DIRECTION_VALUES)[number];

export type TaskSortSelection = `${TaskSortBy}:${TaskSortDirection}`;

export const DEFAULT_TASK_GROUP_BY: TaskGroupBy = 'dueDate';
export const DEFAULT_TASK_SORT_BY: TaskSortBy = 'updatedAt';
export const DEFAULT_TASK_SORT_DIRECTION: TaskSortDirection = 'descending';

export interface TaskGroupByOption {
    description: string;
    icon: IconType;
    label: string;
    value: TaskGroupBy;
}

export const TASK_GROUP_BY_OPTIONS: TaskGroupByOption[] = [
    { value: 'dueDate', label: 'Due Date', description: 'Organize tasks by their due window.', icon: TbCalendarEvent },
    { value: 'status', label: 'Status', description: 'Organize tasks by workflow status.', icon: TbProgress },
    { value: 'priority', label: 'Priority', description: 'Organize tasks by priority.', icon: TbFlag },
    { value: 'assignee', label: 'Assignee', description: 'Organize tasks by assigned members.', icon: TbUsers },
    { value: 'reviewer', label: 'Reviewer', description: 'Organize tasks by reviewing members.', icon: TbUserCheck },
    { value: 'visibility', label: 'Visibility', description: 'Organize tasks by access scope.', icon: TbEye },
    {
        value: 'createdAt',
        label: 'Created Date',
        description: 'Organize tasks by creation date.',
        icon: TbCalendarEvent,
    },
    { value: 'updatedAt', label: 'Updated Date', description: 'Organize tasks by last update date.', icon: TbRefresh },
    { value: 'none', label: 'No Grouping', description: 'Show tasks in one continuous list.', icon: TbLayoutList },
];

export interface TaskSortOption {
    description: string;
    direction: TaskSortDirection;
    icon: IconType;
    label: string;
    sortBy: TaskSortBy;
    value: TaskSortSelection;
}

const taskSortOption = (
    sortBy: TaskSortBy,
    direction: TaskSortDirection,
    label: string,
    description: string,
    icon: IconType,
): TaskSortOption => ({
    description,
    direction,
    icon,
    label,
    sortBy,
    value: `${sortBy}:${direction}`,
});

export const TASK_SORT_OPTIONS: TaskSortOption[] = [
    taskSortOption('updatedAt', 'descending', 'Updated: Newest', 'Show recently updated tasks first.', TbRefresh),
    taskSortOption('updatedAt', 'ascending', 'Updated: Oldest', 'Show least recently updated tasks first.', TbRefresh),
    taskSortOption('dueDate', 'ascending', 'Due Date: Soonest', 'Show the nearest due dates first.', TbSortAscending),
    taskSortOption('dueDate', 'descending', 'Due Date: Latest', 'Show the latest due dates first.', TbSortDescending),
    taskSortOption('title', 'ascending', 'Title: A–Z', 'Sort task titles alphabetically.', TbAlphabetLatin),
    taskSortOption(
        'title',
        'descending',
        'Title: Z–A',
        'Sort task titles in reverse alphabetical order.',
        TbAlphabetLatin,
    ),
    taskSortOption('priority', 'ascending', 'Priority: Highest', 'Show urgent and high-priority tasks first.', TbFlag),
    taskSortOption('priority', 'descending', 'Priority: Lowest', 'Show low-priority tasks first.', TbFlag),
    taskSortOption(
        'status',
        'ascending',
        'Status: Workflow Order',
        'Follow the configured workflow order.',
        TbProgress,
    ),
    taskSortOption(
        'status',
        'descending',
        'Status: Reverse Order',
        'Reverse the configured workflow order.',
        TbProgress,
    ),
    taskSortOption('createdAt', 'descending', 'Created: Newest', 'Show recently created tasks first.', TbCalendarEvent),
    taskSortOption('createdAt', 'ascending', 'Created: Oldest', 'Show earliest created tasks first.', TbCalendarEvent),
];

export interface TaskViewGroup {
    icon: IconType;
    key: string;
    label: string;
    tasks: Task[];
}

const taskGroupByValueSet = new Set<string>(TASK_GROUP_BY_VALUES);
const taskSortByValueSet = new Set<string>(TASK_SORT_BY_VALUES);
const taskSortDirectionValueSet = new Set<string>(TASK_SORT_DIRECTION_VALUES);

export const getTaskGroupBy = (value: unknown): TaskGroupBy =>
    typeof value === 'string' && taskGroupByValueSet.has(value) ? (value as TaskGroupBy) : DEFAULT_TASK_GROUP_BY;

export const getTaskSortBy = (value: unknown): TaskSortBy =>
    typeof value === 'string' && taskSortByValueSet.has(value) ? (value as TaskSortBy) : DEFAULT_TASK_SORT_BY;

export const getTaskSortDirection = (value: unknown): TaskSortDirection =>
    typeof value === 'string' && taskSortDirectionValueSet.has(value)
        ? (value as TaskSortDirection)
        : DEFAULT_TASK_SORT_DIRECTION;

export const getTaskSortSelection = (sortBy: TaskSortBy, direction: TaskSortDirection): TaskSortSelection =>
    `${sortBy}:${direction}`;

const taskBucketMetadata: Record<TaskBucketKey, Pick<TaskViewGroup, 'icon' | 'label'>> = {
    completed: { icon: TbCircleCheckFilled, label: 'Completed' },
    noDueDate: { icon: TbCalendarMinus, label: 'No Due Date' },
    overdue: { icon: TbCalendarMonth, label: 'Overdue' },
    today: { icon: TbCalendarExclamation, label: 'Today' },
    upcoming: { icon: TbCalendarEvent, label: 'Upcoming' },
};

const participantGroupLabel = (task: Task, role: TaskParticipantRole) => {
    const names = task.assignees
        .filter((participant) => participant.role === role)
        .map((participant) => {
            const user = participant.member?.user;
            const name = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';

            return name || user?.emailAddress;
        })
        .filter((value): value is string => Boolean(value))
        .sort((left, right) => left.localeCompare(right));

    return names.length > 0 ? names.join(', ') : role === TaskParticipantRole.ASSIGNEE ? 'Unassigned' : 'No Reviewer';
};

interface TaskGroupMetadata {
    icon: IconType;
    key: string;
    label: string;
    order?: number;
}

const prioritySortOrder: Record<TaskPriority, number> = {
    [TaskPriority.URGENT]: 0,
    [TaskPriority.HIGH]: 1,
    [TaskPriority.MEDIUM]: 2,
    [TaskPriority.LOW]: 3,
};

const getTaskGroupMetadata = (task: Task, groupBy: Exclude<TaskGroupBy, 'dueDate' | 'none'>): TaskGroupMetadata => {
    if (groupBy === 'status') {
        return {
            icon: TbProgress,
            key: task.status?.id ?? 'no-status',
            label: task.status?.name ?? 'No Status',
            order: task.status?.position ?? Number.MAX_SAFE_INTEGER,
        };
    }
    if (groupBy === 'priority') {
        return {
            icon: TbFlag,
            key: task.priority ?? 'no-priority',
            label: task.priority ?? 'No Priority',
            order: task.priority ? prioritySortOrder[task.priority] : Number.MAX_SAFE_INTEGER,
        };
    }
    if (groupBy === 'assignee' || groupBy === 'reviewer') {
        const role = groupBy === 'assignee' ? TaskParticipantRole.ASSIGNEE : TaskParticipantRole.REVIEWER;
        const label = participantGroupLabel(task, role);

        return { icon: groupBy === 'assignee' ? TbUsers : TbUserCheck, key: label, label };
    }
    if (groupBy === 'visibility') {
        const label = task.visibility ?? 'No Visibility';

        return { icon: TbEye, key: label, label };
    }

    const value = groupBy === 'createdAt' ? task.createdAt : task.updatedAt;
    const date = dayjs(value);

    return {
        icon: groupBy === 'createdAt' ? TbCalendarEvent : TbRefresh,
        key: date.format('YYYY-MM-DD'),
        label: date.format('MMM D, YYYY'),
        order: -date.startOf('day').valueOf(),
    };
};

export const groupTasks = (tasks: Task[], groupBy: TaskGroupBy): TaskViewGroup[] => {
    if (groupBy === 'none') {
        return tasks.length > 0
            ? [
                  {
                      icon: TbLayoutList,
                      key: 'all-tasks',
                      label: tasks.every((task) => task.completed) ? 'Completed Tasks' : 'Active Tasks',
                      tasks,
                  },
              ]
            : [];
    }
    if (groupBy === 'dueDate') {
        const buckets = new Map<TaskBucketKey, Task[]>();

        tasks.forEach((task) => {
            const key = taskBucketFor(task);
            buckets.set(key, [...(buckets.get(key) ?? []), task]);
        });

        return TASK_BUCKET_KEYS.flatMap((key) => {
            const groupedTasks = buckets.get(key);

            return groupedTasks?.length ? [{ key, ...taskBucketMetadata[key], tasks: groupedTasks }] : [];
        });
    }

    const groups = new Map<string, TaskViewGroup & { order?: number }>();

    tasks.forEach((task) => {
        const metadata = getTaskGroupMetadata(task, groupBy);
        const group = groups.get(metadata.key);

        if (group) {
            group.tasks.push(task);
        } else {
            groups.set(metadata.key, { ...metadata, tasks: [task] });
        }
    });

    return [...groups.values()]
        .sort((left, right) => {
            if (left.order !== undefined || right.order !== undefined) {
                return (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER);
            }

            return left.label.localeCompare(right.label);
        })
        .map(({ icon, key, label, tasks: groupedTasks }) => ({ icon, key, label, tasks: groupedTasks }));
};

export const sortTasks = (tasks: Task[], sortBy: TaskSortBy, direction: TaskSortDirection): Task[] =>
    [...tasks].sort((left, right) => {
        const compareNullable = <T>(
            leftValue: T | null | undefined,
            rightValue: T | null | undefined,
            compare: (left: T, right: T) => number,
        ) => {
            if ((leftValue === null || leftValue === undefined) && (rightValue === null || rightValue === undefined)) {
                return 0;
            }
            if (leftValue === null || leftValue === undefined) {
                return 1;
            }
            if (rightValue === null || rightValue === undefined) {
                return -1;
            }

            const comparison = compare(leftValue, rightValue);
            return direction === 'ascending' ? comparison : -comparison;
        };
        let comparison = 0;

        if (sortBy === 'title') {
            comparison =
                direction === 'ascending'
                    ? left.title.localeCompare(right.title)
                    : right.title.localeCompare(left.title);
        } else if (sortBy === 'priority') {
            comparison = compareNullable(
                left.priority,
                right.priority,
                (leftPriority, rightPriority) => prioritySortOrder[leftPriority] - prioritySortOrder[rightPriority],
            );
        } else if (sortBy === 'status') {
            comparison = compareNullable(
                left.status,
                right.status,
                (leftStatus, rightStatus) => leftStatus.position - rightStatus.position,
            );
        } else {
            const leftValue =
                sortBy === 'dueDate' ? left.dueAt : sortBy === 'createdAt' ? left.createdAt : left.updatedAt;
            const rightValue =
                sortBy === 'dueDate' ? right.dueAt : sortBy === 'createdAt' ? right.createdAt : right.updatedAt;
            comparison = compareNullable(
                leftValue,
                rightValue,
                (leftDate, rightDate) => dayjs(leftDate).valueOf() - dayjs(rightDate).valueOf(),
            );
        }

        return comparison || left.title.localeCompare(right.title);
    });

const taskDisplayedPropertyKeySet = new Set<string>(TASK_DISPLAYED_PROPERTY_KEYS);

export const isTaskDisplayedPropertyKey = (value: unknown): value is TaskDisplayedPropertyKey =>
    typeof value === 'string' && taskDisplayedPropertyKeySet.has(value);

const uniqueValidKeys = (value: unknown): TaskDisplayedPropertyKey[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(new Set(value.filter(isTaskDisplayedPropertyKey)));
};

export const getTaskPropertyOrder = (value: unknown): TaskDisplayedPropertyKey[] => {
    const savedOrder = uniqueValidKeys(value);
    const savedKeys = new Set(savedOrder);

    return [...savedOrder, ...TASK_DISPLAYED_PROPERTY_KEYS.filter((key) => !savedKeys.has(key))];
};

export const getTaskDisplayedProperties = (
    displayedProperties: unknown,
    propertyOrder: unknown,
): TaskDisplayedPropertyKey[] => {
    const selected =
        displayedProperties === undefined ? DEFAULT_TASK_DISPLAYED_PROPERTIES : uniqueValidKeys(displayedProperties);
    const selectedKeys = new Set(selected);

    return getTaskPropertyOrder(propertyOrder).filter((key) => selectedKeys.has(key));
};
